import { Voice, body, pluck, vary } from '../audio-engine';
/* ============================================================
   配方：有調性電子 ← **使用者 2026-08-05 選定**
   ============================================================

   ## 一句話

   **完全不用噪音**，每個音都是帶包絡的濾波振盪器，音高落在同一個音階上。

   ## 為什麼不刺

   「躁」來自高頻噪音（寬頻、無音高、耳朵無法預測）。純音相反：
   能量集中在一個頻率上，而且**濾波器在前 10 ms 就把高頻收掉** ——
   起音有亮度（聽得出打擊點），之後只剩溫暖的基音。

   ## 音階不是裝飾，是防呆

   主砲每秒 3.75 發、命中每秒十幾次。音高若是隨機的，快速連續的音會撞出
   不協和的音程 —— 那本身就是另一種躁。全部鎖在 **A 小調五聲**之後，
   打得越快越像節奏，不是越像噪音。

   ## 音階外的兩個例外（刻意的）

   `hurt` 與 `alarm` **故意離開音階**（小二度／減五度）。
   壞事就是要聽起來不對勁 —— 全部都在音階上的話，受傷跟升級一樣悅耳，
   玩家就分不出「這是好事還是壞事」。**不協和是資訊，不是瑕疵。**

   ## 已知風險（使用者已知會）

   這個方向最容易滑回「電玩嗶聲」，也就是第一版被否掉的病。
   壓住它的三件事：**低通掃頻**（不留方波的刺）、**兩個微失諧的振盪器**
   （單一振盪器很薄、很像電子錶）、**極短的時長**（大部分 30–90 ms）。 */

/** A 小調五聲，兩個八度多。索引越大越高。 */
var S = [110.0, 130.81, 146.83, 164.81, 196.0, 220.0, 261.63, 293.66, 329.63, 392.0, 440.0];
/** 音階外的兩個「壞消息」音高：小二度與減五度。 */
var BAD = { m2: 116.54, d5: 155.56 };

var step = 0, lastHit = 0;

/* ---------- 主武器（最高頻的三個） ---------- */

/** 主砲：低音撥弦。**固定音高**（音階最低的那個）—— 主砲是底，
    會動的東西交給命中。 */
export function shoot(v: Voice) {
  pluck({
    bus: 'weapon', x: v.x, z: v.z, vol: 0.85, f: vary(S[0], 0.015),
    dur: vary(0.075, 0.12), cut0: 2200, cut1: 420, type: 'triangle', detune: 9
  });
}

/** 命中：往上爬的短撥弦。

    連續命中沿音階往上走，停手 0.6 秒之後回到起點 ——
    這讓「連打同一隻」聽起來是有進展的，而不是同一個音重複十幾次。 */
export function hit(v: Voice) {
  var now = typeof performance !== 'undefined' ? performance.now() : 0;
  if (now - lastHit > 600) step = 0;
  lastHit = now;
  var f = S[4 + (step % 6)];
  step++;
  pluck({
    bus: 'enemy', x: v.x, z: v.z, vol: 0.42, f: vary(f, 0.01),
    dur: vary(0.032, 0.2), cut0: 3000, cut1: 900, type: 'triangle'
  });
}

/** 擊殺：撥弦 + 往下掉的低頻，落在根音上 —— 讀起來是「一句話講完了」。 */
export function kill(v: Voice) {
  pluck({
    bus: 'enemy', x: v.x, z: v.z, vol: 0.8, f: S[3],
    dur: vary(0.12, 0.12), cut0: 2600, cut1: 380, type: 'triangle', detune: 14
  });
  body({ bus: 'enemy', x: v.x, z: v.z, vol: 0.9, dur: vary(0.18, 0.12), f0: vary(110, 0.06), f1: 40, type: 'sine', drop: 0.45 });
}

/* ---------- 副武器與大招 ---------- */

/** 排一個延遲的音。**不吃 `timeScale`** —— 音效本來就不該跟著頓幀慢下來
    （頓幀是視覺的節拍，聲音慢下來會讀成掉幀）。 */
function at(ms: number, fn: () => void) { setTimeout(fn, ms); }

/** 飛彈齊射：**下行**兩音 —— 東西離開你身邊就是往下走。 */
export function missile(v: Voice) {
  pluck({ bus: 'weapon', x: v.x, z: v.z, vol: 0.7, f: S[5], dur: 0.09, cut0: 2400, cut1: 500, type: 'triangle', detune: 11 });
  at(65, function () {
    pluck({ bus: 'weapon', x: v.x, z: v.z, vol: 0.62, f: S[2], dur: 0.14, cut0: 1800, cut1: 380, type: 'triangle', detune: 11 });
  });
  body({ bus: 'weapon', x: v.x, z: v.z, vol: 0.6, dur: 0.13, f0: 95, f1: 42, type: 'sine' });
}

/** 大招：**三個音一起**（根音 + 五度 + 八度）＋ 一條長低頻。

    全場唯一的和弦。理由是編制上的對比 —— 其他每一個音都是單音，
    所以三個音同時出現本身就是「這一下不一樣」的訊號，
    不需要靠音量把別的東西壓掉。 */
export function beam(v: Voice) {
  var F = [S[0], S[3], S[5]];
  for (var i = 0; i < F.length; i++) {
    pluck({
      bus: 'weapon', x: v.x, z: v.z, vol: 0.55, f: F[i],
      dur: 0.55 - i * 0.08, cut0: 3000, cut1: 460, type: 'triangle', detune: 8 + i * 4
    });
  }
  body({ bus: 'weapon', x: v.x, z: v.z, vol: 0.85, dur: 0.6, f0: 110, f1: 46, type: 'sine', drop: 0.35 });
}

/** 斬擊：快速上行兩音，最短的一個。 */
export function slash(v: Voice) {
  /* `v.step` 是連段／連續命中的升階（見 `audio-engine.ts` 的 Voice）。
     往上取**音階**而不是加半音 —— 留在調上，連段聽起來才是一句話。
     基底是 S[7]/S[9]，夾住上界免得升到陣列外。 */
  var d = Math.max(0, Math.min(2, v.step || 0));
  var a0 = S[Math.min(S.length - 1, 7 + d)], a1 = S[Math.min(S.length - 1, 9 + d)];
  var lift = 1 + d * 0.14;
  pluck({ bus: 'weapon', x: v.x, z: v.z, vol: 0.55 * lift, f: vary(a0, 0.01), dur: 0.045, cut0: 3400, cut1: 1100, type: 'triangle' });
  at(38, function () {
    pluck({ bus: 'weapon', x: v.x, z: v.z, vol: 0.45 * lift, f: vary(a1, 0.01), dur: 0.05, cut0: 3600, cut1: 1200, type: 'triangle' });
  });
}

/* ---------- 玩家的動作與狀態 ---------- */

/** 衝刺：一個很輕的高音，幾乎只是氣音。 */
export function dash(v: Voice) {
  pluck({ bus: 'ui', x: v.x, z: v.z, vol: 0.30, f: S[9], dur: 0.05, cut0: 2800, cut1: 1400, type: 'sine' });
}

/** 受傷：**離開音階**（小二度）＋ 大量失諧。

    這是全遊戲最該讓人不舒服的一個音。它跟其他所有聲音的關係是「不協和」，
    所以在一團戰鬥聲裡它會自己跳出來 —— 靠的是音程不是音量。 */
export function hurt(v: Voice) {
  pluck({ bus: 'ui', x: v.x, z: v.z, vol: 0.8, f: BAD.m2, dur: 0.16, cut0: 1600, cut1: 300, type: 'triangle', detune: 34 });
  body({ bus: 'ui', x: v.x, z: v.z, vol: 0.75, dur: 0.2, f0: 98, f1: 38, type: 'sine' });
}

/** 復活：低根音緩緩起來 + 八度。全遊戲最長的一個音。 */
export function revive(v: Voice) {
  pluck({ bus: 'ui', x: v.x, z: v.z, vol: 0.7, f: S[0], dur: 0.9, cut0: 1400, cut1: 300, type: 'triangle', detune: 6 });
  at(160, function () {
    pluck({ bus: 'ui', x: v.x, z: v.z, vol: 0.55, f: S[5], dur: 0.7, cut0: 2000, cut1: 400, type: 'triangle', detune: 10 });
  });
}

/* ---------- 事件與介面 ---------- */

/** 爆炸：最重的低頻。撥弦只當起音的顆粒。 */
export function boom(v: Voice) {
  pluck({ bus: 'world', x: v.x, z: v.z, vol: 0.6, f: S[0], dur: 0.2, cut0: 1500, cut1: 260, type: 'triangle', detune: 18 });
  body({ bus: 'world', x: v.x, z: v.z, vol: 1.0, dur: 0.30, f0: vary(100, 0.08), f1: 30, type: 'sine', drop: 0.35 });
}

/** 升級：根音 → 五度，兩個音。**上行＝好事**，這是全套的基本語法。 */
export function level(v: Voice) {
  pluck({ bus: 'ui', x: v.x, z: v.z, vol: 0.6, f: S[5], dur: 0.22, cut0: 2800, cut1: 700, type: 'triangle', detune: 7 });
  at(95, function () {
    pluck({ bus: 'ui', x: v.x, z: v.z, vol: 0.55, f: S[8], dur: 0.28, cut0: 3000, cut1: 800, type: 'triangle', detune: 7 });
  });
}

/** 進化前置達成：三音琶音（根、三度、五度）。比升級**多一個音**，
    那就是「這次不一樣」—— 階差用音數表達，不用音量。 */
export function preEvo(v: Voice) {
  var F = [S[5], S[6], S[8]];
  for (var i = 0; i < F.length; i++) {
    (function (f, k) {
      at(k * 70, function () {
        pluck({ bus: 'ui', x: v.x, z: v.z, vol: 0.55, f: f, dur: 0.26, cut0: 3200, cut1: 900, type: 'triangle', detune: 6 });
      });
    })(F[i], i);
  }
}

/** 傳送門：兩個音疊著長長地開，是全套最「空」的一個。 */
export function portal(v: Voice) {
  pluck({ bus: 'world', x: v.x, z: v.z, vol: 0.5, f: S[2], dur: 0.75, cut0: 1800, cut1: 320, type: 'sine', detune: 5 });
  pluck({ bus: 'world', x: v.x, z: v.z, vol: 0.4, f: S[7], dur: 0.6, cut0: 2200, cut1: 420, type: 'sine', detune: 5 });
}

/** 警報：**離開音階**（減五度）並重複兩次。

    重複是警報的語法（單次讀成「某件事發生了」，重複才讀成「注意」），
    而減五度讓它跟戰鬥中的任何音都不協和 —— Boss 進場時它必須穿得過去。 */
export function alarm(v: Voice) {
  for (var i = 0; i < 2; i++) {
    (function (k) {
      at(k * 210, function () {
        pluck({ bus: 'ui', x: v.x, z: v.z, vol: 0.62, f: k ? BAD.d5 : S[3], dur: 0.18, cut0: 2600, cut1: 600, type: 'triangle', detune: 22 });
      });
    })(i);
  }
}
